import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, computed } from '@angular/core';
import { PendingChanges } from '@guards/pending-changes.guard';
import { BehaviorSubject, combineLatest, debounceTime, firstValueFrom, Subject, takeUntil } from 'rxjs';
import {
  CustomFieldDetailDto,
  CustomFieldItemDto,
  CustomFieldMergeDto,
  CustomFieldService,
} from '@artemis-software/wr-api';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import {
  ListDetailSplitViewComponent,
} from '@components/split-view/list-detail-split-view/list-detail-split-view.component';
import { catalogTenantId } from '@/utils/admin-utils';
import { CurrentUserService } from '@services/current-user.service';

@Component({
  standalone: false,
  selector: 'wr-custom-field-split-view',
  templateUrl: './custom-field-split-view.component.html',
  styleUrls: ['./custom-field-split-view.component.scss'],
})
export class CustomFieldSplitViewComponent implements PendingChanges, OnInit, OnDestroy {

  @ViewChild(ListDetailSplitViewComponent)
  splitView?: ListDetailSplitViewComponent<CustomFieldItemDto, CustomFieldDetailDto>;

  @ViewChild('folderDialog') folderDialog?: TemplateRef<unknown>;

  readonly loadDetail = (item: CustomFieldItemDto) => this.customFieldService.findById(item.id);
  readonly saveAction = (val: CustomFieldMergeDto) => this.save(val);
  readonly deleteAction = (val: CustomFieldMergeDto) => this.delete(val.id!);
  readonly deleteDialogAction = () => this.deleteDialog();

  readonly currentTenantId = catalogTenantId();
  readonly canCreate = computed(() => this.currentUserService.hasPrivilege('canCreateGenericForms'));

  loading$ = new BehaviorSubject<boolean>(false);
  searchText$ = new BehaviorSubject<string>('');
  reload$ = new BehaviorSubject<void>(undefined);
  fields$ = new BehaviorSubject<CustomFieldItemDto[] | undefined>(undefined);
  folderPath$ = new BehaviorSubject<string>('');
  subfolders: { name: string; count: number }[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private readonly customFieldService: CustomFieldService,
    private readonly nbDialogService: NbDialogService,
    private readonly nbToastrService: NbToastrService,
    private readonly currentUserService: CurrentUserService,
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.searchText$,
      this.reload$,
      this.folderPath$,
    ]).pipe(
      debounceTime(300),
      takeUntil(this.destroy$),
    ).subscribe(async ([searchText, , folderPath]) => {
      this.fields$.next(undefined);
      this.splitView?.unselect();
      const category = this.normalizePath(folderPath);
      const [fields, folderNames, allItems] = await Promise.all([
        firstValueFrom(this.customFieldService.findAll({
          text: searchText.trim(),
          tenantId: this.currentTenantId(),
          sort: { sort: 'name', order: 'ASC' },
          category,
        })),
        firstValueFrom(this.customFieldService.getFolders(category || undefined)),
        firstValueFrom(this.customFieldService.findAll({ tenantId: this.currentTenantId() })),
      ]);
      this.fields$.next(fields);

      this.subfolders = folderNames.map(name => {
        const prefix = this.normalizePath(category ? `${category.replace(/\/$/, '')}/${name}` : name);
        const count = allItems.filter(item => item.category.startsWith(prefix)).length;
        return { name, count };
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  hasPendingChanges(): boolean | Promise<boolean> {
    return false;
  }

  get pathSegments(): string[] {
    const p = this.folderPath$.value;
    return p ? p.split('/').filter(s => s.length > 0) : [];
  }

  normalizePath(path: string): string {
    const trimmed = path.trim().replace(/^\/+|\/+$/g, '');
    return trimmed ? `${trimmed}/` : '';
  }

  navigateToRoot(): void {
    this.folderPath$.next('');
  }

  navigateToSegment(index: number): void {
    this.folderPath$.next(this.pathSegments.slice(0, index + 1).join('/'));
  }

  navigateToFolder(folderName: string): void {
    const current = this.folderPath$.value;
    this.folderPath$.next(current ? `${current}/${folderName}` : folderName);
  }

  openFolderDialog(): void {
    if (this.folderDialog) {
      this.nbDialogService.open(this.folderDialog);
    }
  }

  get currentFolderPath(): string {
    return this.normalizePath(this.folderPath$.value);
  }

  showNewForm(): void {
    this.splitView?.showNewItem();
  }

  async save(formValue: CustomFieldMergeDto): Promise<void> {
    await firstValueFrom(this.customFieldService.merge(formValue));
    this.nbToastrService.success('Benutzerdefiniertes Feld gespeichert', 'Erfolg');
    this.reload$.next(undefined);
  }

  async deleteDialog(): Promise<boolean> {
    return await firstValueFrom(this.nbDialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Benutzerdefiniertes Feld löschen?',
        message: 'Möchten Sie das benutzerdefinierte Feld wirklich löschen?',
      },
    }).onClose);
  }

  async delete(id: string): Promise<void> {
    await firstValueFrom(this.customFieldService.deleteById(id));
    this.nbToastrService.success('Benutzerdefiniertes Feld gelöscht', 'Erfolg');
    this.reload$.next(undefined);
    this.splitView?.unselect();
  }
}
